import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faBuilding,
  faCheckCircle,
  faCircleHalfStroke,
  faClockRotateLeft,
  faEnvelope,
  faHeadset,
  faHourglassHalf,
  faIdBadge,
  faLayerGroup,
  faPhone,
  faPlus,
  faSpinner,
  faTicket,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
} from "recharts";
import { getlist, post } from "../../contexts/apicontexts";
import config from "../../config";

const BRAND = "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)";

const STATUS_COLORS = {
  "new":         "#6366f1",
  "in progress": "#3b82f6",
  "on hold":     "#f59e0b",
  "resolved":    "#10b981",
  "closed":      "#64748b",
};
const colorForStatus = (s) => STATUS_COLORS[String(s ?? "").toLowerCase()] ?? "#94a3b8";

const safeParseJson = (v) => { try { return JSON.parse(v); } catch { return null; } };

const formatDate = (v) => {
  if (!v) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "2-digit" }).format(new Date(v));
  } catch { return String(v); }
};

const formatRelative = (v) => {
  if (!v) return "—";
  try {
    const diff = Date.now() - new Date(v).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return formatDate(v);
  } catch { return formatDate(v); }
};

// ── Reusable pieces ────────────────────────────────────────────────────────────

const KpiCard = ({ icon, label, value, sub, accent }) => (
  <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-[#161B22]">
    <div className="flex items-start gap-4 p-5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-sm" style={{ background: accent ?? BRAND }}>
        <FontAwesomeIcon icon={icon} className="text-base" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">{label}</div>
        <div className="mt-0.5 text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{value}</div>
        {sub ? <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{sub}</div> : null}
      </div>
    </div>
    <div className="h-0.5 w-full" style={{ background: accent ?? BRAND }} />
  </div>
);

const ChartCard = ({ title, icon, children, className = "" }) => (
  <div className={`overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-[#161B22] ${className}`}>
    <div className="flex items-center gap-2.5 border-b border-gray-100 px-5 py-3.5 dark:border-gray-800">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg text-white text-xs" style={{ background: BRAND }}>
        <FontAwesomeIcon icon={icon} />
      </span>
      <h3 className="text-sm font-bold tracking-tight text-slate-800 dark:text-white">{title}</h3>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const c = colorForStatus(status);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold"
      style={{ borderColor: `${c}55`, background: `${c}18`, color: c }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
      {status}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const map = { low: "#10b981", medium: "#f59e0b", high: "#ef4444" };
  const c = map[String(priority ?? "").toLowerCase()] ?? "#94a3b8";
  return (
    <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
      style={{ borderColor: `${c}40`, background: `${c}15`, color: c }}>
      {priority}
    </span>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-xs shadow-xl">
      {label ? <div className="mb-1 font-bold text-white">{label}</div> : null}
      {payload.map((p) => (
        <div key={p.dataKey ?? p.name} className="flex items-center gap-2 text-gray-300">
          <span className="h-2 w-2 rounded-full" style={{ background: p.fill ?? p.color }} />
          {p.name}: <span className="font-semibold text-white">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const CustomPieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { name, value, payload: p } = payload[0];
  return (
    <div className="rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 text-xs shadow-xl">
      <div className="flex items-center gap-2 font-bold text-white">
        <span className="h-2 w-2 rounded-full" style={{ background: p.fill }} />
        {name}
      </div>
      <div className="mt-0.5 text-gray-300">{value} tickets</div>
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

export default function CustomerUserDashboard({ displayName }) {
  const user = useMemo(() => safeParseJson(localStorage.getItem("user")), []);

  const [profile, setProfile] = useState(null);
  const [ticketSummary, setTicketSummary] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const userId = user?.userId ?? user?.UserId ?? user?.id ?? user?.Id ?? "";

        const [ticketPagedRes, profileRes, analyticsRes] = await Promise.allSettled([
          post(`${config.baseurl}Ticket/getpaged`, { PageNumber: 1, PageSize: 5 }),
          getlist(`${config.baseurl}CustomerUser/getlist`),
          post(`${config.baseurl}TicketAnalyticsReport/dashboard`, {
            CustomerUserId: userId,
          }),
        ]);

        if (cancelled) return;

        if (ticketPagedRes.status === "fulfilled" && ticketPagedRes.value?.status === 200) {
          const data = ticketPagedRes.value.data ?? {};
          setTicketSummary(data.summary ?? data.Summary ?? null);
          const items = Array.isArray(data.items ?? data.Items) ? (data.items ?? data.Items) : [];
          setRecentTickets(items.slice(0, 5));
        }

        if (profileRes.status === "fulfilled" && profileRes.value?.status === 200) {
          const list = Array.isArray(profileRes.value.data) ? profileRes.value.data : [];
          const me = list.find((u) =>
            (u.userId ?? u.UserId) === userId ||
            String(u.email ?? u.Email ?? "").toLowerCase() === String(user?.email ?? "").toLowerCase()
          ) ?? list[0] ?? null;
          setProfile(me);
        }

        if (analyticsRes.status === "fulfilled" && analyticsRes.value?.status === 200) {
          setAnalytics(analyticsRes.value.data ?? null);
        }
      } catch {
        if (!cancelled) setError("Dashboard data could not be loaded.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user]);

  // Derived counts from ticket summary
  const counts = useMemo(() => {
    const s = ticketSummary ?? {};
    const total    = Number(s.total    ?? s.Total    ?? 0);
    const newCount = Number(s.new      ?? s.New      ?? 0);
    const inProg   = Number(s.inProgress ?? s.InProgress ?? 0);
    const onHold   = Number(s.onHold   ?? s.OnHold   ?? 0);
    const resolved = Number(s.resolved ?? s.Resolved ?? 0);
    const unread   = Number(s.unreadTicketCount ?? s.UnreadTicketCount ?? 0);
    return { total, newCount, inProg, onHold, resolved, unread, open: newCount + inProg + onHold };
  }, [ticketSummary]);

  // Status pie data from live counts
  const statusPieData = useMemo(() => {
    const arr = [
      { name: "New",         value: counts.newCount, fill: colorForStatus("new") },
      { name: "In Progress", value: counts.inProg,   fill: colorForStatus("in progress") },
      { name: "On Hold",     value: counts.onHold,   fill: colorForStatus("on hold") },
      { name: "Resolved",    value: counts.resolved, fill: colorForStatus("resolved") },
    ].filter((d) => d.value > 0);
    return arr;
  }, [counts]);

  // Analytics trend
  const statusBreakdown = useMemo(() => {
    const raw = analytics?.statusBreakdown ?? analytics?.StatusBreakdown ?? [];
    return Array.isArray(raw) ? raw.map((s) => ({
      name:    String(s.label ?? s.Label ?? s.status ?? s.Status ?? ""),
      value:   Number(s.value ?? s.Value ?? s.count ?? s.Count ?? 0),
      fill:    colorForStatus(s.label ?? s.Label ?? s.status ?? s.Status ?? ""),
    })).filter((s) => s.value > 0) : [];
  }, [analytics]);

  const trend = useMemo(() => {
    const raw = analytics?.ticketTrend ?? analytics?.TicketTrend ?? [];
    return Array.isArray(raw) ? raw.slice(-12).map((p) => ({
      label:   String(p.label ?? p.Label ?? ""),
      tickets: Number(p.tickets ?? p.Tickets ?? p.value ?? p.Value ?? 0),
    })) : [];
  }, [analytics]);

  if (loading) return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-slate-500 dark:text-slate-400">
        <FontAwesomeIcon icon={faSpinner} spin className="text-2xl" style={{ color: "#C04C2A" }} />
        <span className="text-sm font-medium">Loading your dashboard…</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 dark:border-rose-800 dark:bg-rose-900/20">
      <div className="flex items-center gap-3 text-rose-600 dark:text-rose-400">
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <span className="text-sm font-semibold">{error}</span>
      </div>
    </div>
  );

  const name = profile?.fullName ?? profile?.FullName ?? user?.fullName ?? displayName ?? "User";
  const customerName = profile?.customerName ?? profile?.CustomerName ?? "";
  const orgName = profile?.organizationName ?? profile?.OrganizationName ?? "";

  return (
    <div className="space-y-5">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-2xl text-white shadow-lg" style={{ background: BRAND }}>
        <div className="pointer-events-none absolute -right-14 -top-14 h-52 w-52 rounded-full bg-white/5 blur-3xl" />
        <div className="pointer-events-none absolute -left-8 bottom-0 h-32 w-32 rounded-full blur-2xl" style={{ background: "rgba(255,160,100,0.12)" }} />
        <div className="h-[3px] w-full" style={{ background: "linear-gradient(to right, rgba(255,255,255,0.35), rgba(255,255,255,0.05) 60%, transparent)" }} />

        <div className="relative flex flex-col gap-4 px-5 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-7">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-orange-100">
              <FontAwesomeIcon icon={faHeadset} className="text-[9px]" />
              Customer Portal
            </div>
            <h1 className="mt-2 text-xl font-extrabold tracking-tight drop-shadow-sm sm:text-2xl">Welcome, {name}</h1>
            <div className="mt-1 flex flex-wrap gap-2 text-xs text-orange-100">
              {customerName ? (
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faIdBadge} className="text-[10px]" />
                  {customerName}
                </span>
              ) : null}
              {orgName ? (
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faBuilding} className="text-[10px]" />
                  {orgName}
                </span>
              ) : null}
            </div>
            {counts.unread > 0 ? (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-amber-400/20 px-3 py-1 text-xs font-bold text-amber-100">
                {counts.unread} unread {counts.unread === 1 ? "reply" : "replies"} waiting
              </div>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-wrap gap-2">
            <Link
              to="/tickets"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-xs font-bold shadow-sm transition hover:bg-orange-50"
              style={{ color: "#C04C2A" }}
            >
              <FontAwesomeIcon icon={faTicket} />
              My Tickets
            </Link>
            <Link
              to="/tickets"
              className="inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/15 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-sm transition hover:bg-white/25"
            >
              <FontAwesomeIcon icon={faPlus} />
              New Ticket
            </Link>
          </div>
        </div>

        {/* Status strip */}
        <div className="relative grid grid-cols-2 gap-px border-t border-white/10 bg-white/10 sm:grid-cols-4">
          {[
            { label: "Total",       value: counts.total,    color: "#fff" },
            { label: "Open",        value: counts.open,     color: "#fbbf24" },
            { label: "Resolved",    value: counts.resolved, color: "#34d399" },
            { label: "Unread",      value: counts.unread,   color: "#a78bfa" },
          ].map((item) => (
            <div key={item.label} className="bg-white/5 px-4 py-3 text-center">
              <div className="text-xl font-extrabold" style={{ color: item.color }}>{item.value}</div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-orange-100">{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard
          icon={faLayerGroup}
          label="Total Tickets"
          value={counts.total}
          sub="All time"
          accent="linear-gradient(135deg,#C04C2A,#4A1B13)"
        />
        <KpiCard
          icon={faHourglassHalf}
          label="Open Tickets"
          value={counts.open}
          sub="New + In Progress + On Hold"
          accent="linear-gradient(135deg,#f59e0b,#d97706)"
        />
        <KpiCard
          icon={faCheckCircle}
          label="Resolved"
          value={counts.resolved}
          sub="Completed by support"
          accent="linear-gradient(135deg,#10b981,#059669)"
        />
        <KpiCard
          icon={faHeadset}
          label="Unread Replies"
          value={counts.unread}
          sub="Waiting for your review"
          accent={counts.unread > 0 ? "linear-gradient(135deg,#8b5cf6,#6d28d9)" : "linear-gradient(135deg,#64748b,#475569)"}
        />
      </div>

      {/* ── Charts row ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">

        {/* Pie: live counts */}
        <ChartCard title="Status Breakdown" icon={faCircleHalfStroke}>
          {statusPieData.length ? (
            <>
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={statusPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={46} outerRadius={70} paddingAngle={3}>
                    {statusPieData.map((e) => <Cell key={e.name} fill={e.fill} />)}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 flex flex-col gap-1.5">
                {statusPieData.map((s) => (
                  <div key={s.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: s.fill }} />
                      <span className="font-medium text-slate-700 dark:text-slate-300">{s.name}</span>
                    </div>
                    <span className="font-bold text-slate-900 dark:text-white">{s.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex h-40 items-center justify-center text-xs text-slate-400 dark:text-slate-500">
              No tickets yet
            </div>
          )}
        </ChartCard>

        {/* Trend line */}
        <ChartCard title="Ticket Trend" icon={faArrowTrendUp} className="lg:col-span-2">
          {trend.length ? (
            <ResponsiveContainer width="100%" height={210}>
              <LineChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9ca3af" }} />
                <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="tickets" name="Tickets" stroke="#C04C2A" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[210px] items-center justify-center text-xs text-slate-400 dark:text-slate-500">
              No trend data yet
            </div>
          )}
        </ChartCard>
      </div>

      {/* ── Analytics bar chart ── */}
      {statusBreakdown.length ? (
        <ChartCard title="Tickets by Status (Analytics)" icon={faTicket}>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={statusBreakdown} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#9ca3af" }} />
              <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" name="Tickets" radius={[6, 6, 0, 0]}>
                {statusBreakdown.map((e) => <Cell key={e.name} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      ) : null}

      {/* ── Recent tickets + Profile ── */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">

        {/* Recent tickets table */}
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-[#161B22] lg:col-span-3">
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-3.5 dark:border-gray-800">
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg text-white text-xs" style={{ background: BRAND }}>
                <FontAwesomeIcon icon={faClockRotateLeft} />
              </span>
              <h3 className="text-sm font-bold tracking-tight text-slate-800 dark:text-white">Recent Tickets</h3>
            </div>
            <Link to="/tickets" className="text-xs font-semibold hover:underline" style={{ color: "#C04C2A" }}>
              View all →
            </Link>
          </div>

          {recentTickets.length ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {recentTickets.map((t) => {
                const id     = t.id ?? t.Id ?? 0;
                const num    = t.ticketNumber ?? t.TicketNumber ?? `#${id}`;
                const subj   = t.subject ?? t.Subject ?? "Untitled";
                const status = t.status ?? t.Status ?? "—";
                const prio   = t.priority ?? t.Priority ?? "";
                const date   = t.lastReplyAt ?? t.LastReplyAt ?? t.createdAt ?? t.CreatedAt ?? null;
                const unread = Number(t.unreadCount ?? t.UnreadCount ?? 0);
                return (
                  <Link key={id} to={`/tickets/${id}`}
                    className="flex items-start gap-3 px-5 py-3.5 transition hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white text-[10px] font-bold" style={{ background: BRAND }}>
                      <FontAwesomeIcon icon={faTicket} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-400 dark:text-slate-500">{num}</span>
                        {unread > 0 ? (
                          <span className="rounded-full bg-violet-500 px-1.5 py-0.5 text-[9px] font-bold text-white">{unread}</span>
                        ) : null}
                        {prio ? <PriorityBadge priority={prio} /> : null}
                      </div>
                      <div className="mt-0.5 truncate text-sm font-semibold text-slate-800 dark:text-white">{subj}</div>
                      <div className="mt-0.5 text-[10px] text-slate-400 dark:text-slate-500">{formatRelative(date)}</div>
                    </div>
                    <StatusBadge status={status} />
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: BRAND }}>
                <FontAwesomeIcon icon={faTicket} />
              </div>
              <div className="text-sm font-semibold text-slate-600 dark:text-slate-400">No tickets yet</div>
              <Link
                to="/tickets"
                className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-xs font-bold text-white transition hover:opacity-90"
                style={{ background: BRAND }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Create your first ticket
              </Link>
            </div>
          )}
        </div>

        {/* Profile card */}
        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-700 dark:bg-[#161B22] lg:col-span-2">
          <div className="flex items-center gap-2.5 border-b border-gray-100 px-5 py-3.5 dark:border-gray-800">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg text-white text-xs" style={{ background: BRAND }}>
              <FontAwesomeIcon icon={faIdBadge} />
            </span>
            <h3 className="text-sm font-bold tracking-tight text-slate-800 dark:text-white">My Profile</h3>
          </div>

          {/* Avatar initials */}
          <div className="flex flex-col items-center gap-2 border-b border-gray-100 px-5 py-5 dark:border-gray-800">
            <div className="flex h-14 w-14 items-center justify-center rounded-full text-lg font-extrabold text-white shadow" style={{ background: BRAND }}>
              {String(name).trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("")}
            </div>
            <div className="text-center">
              <div className="text-sm font-bold text-slate-900 dark:text-white">{name}</div>
              {customerName ? <div className="text-xs text-slate-500 dark:text-slate-400">{customerName}</div> : null}
              {orgName ? <div className="text-xs text-slate-400 dark:text-slate-500">{orgName}</div> : null}
            </div>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {[
              { icon: faEnvelope, label: "Email",       value: profile?.email ?? profile?.Email ?? user?.email ?? "—" },
              { icon: faPhone,    label: "Mobile",       value: profile?.mobile ?? profile?.Mobile ?? "—" },
              { icon: faIdBadge,  label: "Designation",  value: profile?.designation ?? profile?.Designation ?? "—" },
              { icon: faBuilding, label: "Department",   value: profile?.department ?? profile?.Department ?? "—" },
              { icon: faClockRotateLeft, label: "Last Login", value: formatDate(profile?.lastLoginAt ?? profile?.LastLoginAt) },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-3 px-5 py-2.5">
                <FontAwesomeIcon icon={row.icon} className="w-3.5 shrink-0 text-slate-400 dark:text-slate-500" />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{row.label}</div>
                  <div className="truncate text-xs font-semibold text-slate-800 dark:text-white">{row.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
}
